import React, { useState, useEffect, useCallback, useRef } from 'react';
import { type StatusTone } from '../utils/statusColors';
import { CELULA, CascaDaLista, TabelaDaLista, CabecalhoDaLista, ColunaDaLista, LinhaDaLista, CelulaStatus, CelulaAcoes, CartaoDoTopo } from './shared/lista/PecasDaLista';
import { SIGNERS } from '../config/company';
import { oficiosService, clientsService, contractsService, contractAdjustmentsService } from '../services/api';
import { fmtDate } from '../utils/formatters';
import { fmtPct as fmtPctAdj, fmtBRL as fmtBRLAdj, adjustmentStatusMeta } from '../utils/adjustments';
import { Icon } from './shared/Icon';
import { usePedirTexto, useConfirmarPerigo, useConfirm } from './shared/ConfirmDialog';
import { useAviso } from './shared/Aviso';
import { relatoDoLembrete, relatoDaFalhaDoLembrete } from '../utils/lembrete';
import CascaDeModal from './shared/CascaDeModal';
import { DocumentViewerModal, DocViewerInfoItem, DocViewerEvent } from './shared/DocumentViewerModal';
import { campo, campoSomenteLeitura, useEstadoDaCarga, EstadoDaLista, A_PROPRIA_LISTA_DIZ_O_VAZIO, textos, mensagemDoErroDeCarga } from '../design';
import { TONE_TEXT } from '../utils/statusColors';
import { FRASE_DO_EMITIDO, textoTravado, payloadDaEdicao, porqueNaoOficializar, fraseDaAnualidadeQueNaoCarregou, confirmacaoDeEmitirOficio, confirmacaoDeCancelarOficio, confirmacaoDeReabrirOficio, rotuloDoPdf } from '../utils/administrativo';
import { nomeDeExibicao } from '../utils/nomeDeExibicao';



const fmt = (v: any) => {
  const n = parseFloat(String(v));
  return isNaN(n) ? '0,00' : n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

interface Oficio {
  id: number;
  numero: string;
  ano: number;
  sequencial: number;
  tipo: string;
  status: string;
  clientId?: number;
  client?: { id: number; tradeName?: string; companyName?: string };
  contractId?: number;
  contract?: { id: number; code?: string; contractNumber?: string };
  destinatarioNome: string;
  destinatarioEndereco?: string;
  destinatarioCidade?: string;
  destinatarioCep?: string;
  destinatarioEmail?: string;
  // Como o ofício é entregue, DECLARADO por quem opera (backend #362). `email` é o default da coluna,
  // então ofício antigo sem o campo continua significando o que significava.
  destinoTipo?: DestinoTipo;
  destinoPortalUrl?: string | null;
  referenciaOficio?: string;
  referenciaProcesso?: string;
  assunto: string;
  corpo: string;
  valorAtual?: number;
  indiceReajuste?: number;
  indiceNome?: string;
  valorReajustado?: number;
  dataBaseReajuste?: string;
  pdfBase64?: string;
  clicksignDocumentKey?: string;
  // `clicksignSignerKey` é o request_signature_key do ClickSign: monta o link de assinatura
  // daquela pessoa (ver `linkDeAssinatura` em shared/DocumentViewerModal).
  signers?: Array<{ name: string; email: string; cpf?: string; role?: string; signedAt?: string; clicksignSignerKey?: string }>;
  signedAt?: string;
  attachments?: OficioAttachment[];
  createdAt: string;
}

/**
 * Os dois caminhos de entrega que o backend aceita (enum `oficio_destino_tipo`). A guarda de emissão e
 * de envio para assinatura exige o campo do destino DECLARADO: e-mail pede "E-mail do destinatario",
 * portal pede "Link da compra no portal". Os rótulos da tela são essas palavras, porque é para elas que a
 * mensagem de recusa do backend aponta.
 */
export type DestinoTipo = 'email' | 'portal';
export const DESTINOS: { valor: DestinoTipo; rotulo: string }[] = [
  { valor: 'email', rotulo: 'Por e-mail' },
  { valor: 'portal', rotulo: 'Anexo no portal da compra' },
];

/**
 * O que vai para o backend sobre o destino. Com destino e-mail, o link do portal não se aplica e não
 * fica visível; mandá-lo assim mesmo gravaria um valor que ninguém vê. Por isso vai `null`.
 */
export function camposDeDestino(form: { destinoTipo?: DestinoTipo; destinatarioEmail?: string; destinoPortalUrl?: string | null }) {
  const destinoTipo: DestinoTipo = form.destinoTipo === 'portal' ? 'portal' : 'email';
  return {
    destinoTipo,
    destinatarioEmail: String(form.destinatarioEmail ?? '').trim(),
    destinoPortalUrl: destinoTipo === 'portal' ? String(form.destinoPortalUrl ?? '').trim() : null,
  };
}

/**
 * A linha "Entrega" do painel do ofício. Destino declarado sem o campo dele fica em aviso: é exatamente o
 * estado em que a emissão e o envio para assinatura são recusados.
 */
export function entregaDoOficio(o: { destinoTipo?: DestinoTipo; destinatarioEmail?: string; destinoPortalUrl?: string | null }) {
  if (o.destinoTipo === 'portal') {
    const url = String(o.destinoPortalUrl ?? '').trim();
    return url
      ? { label: 'Entrega', value: <a href={url} target='_blank' rel='noopener noreferrer' className='underline break-all'>Anexo no portal da compra</a> }
      : { label: 'Entrega', value: 'Portal, sem o link da compra', tone: 'warning' as const };
  }
  const email = String(o.destinatarioEmail ?? '').trim();
  return email
    ? { label: 'Entrega', value: `Por e-mail: ${email}` }
    : { label: 'Entrega', value: 'Por e-mail, sem endereço', tone: 'warning' as const };
}

interface OficioAttachment {
  id: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  uploadedAt: string;
  uploadedBy?: string;
  s3Key: string;
}

interface Client {
  id: number;
  name?: string;
  tradeName?: string;
  companyName?: string;
  cnpj?: string;
  street?: string;
  number?: string;
  complement?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

interface Contract {
  id: number;
  code?: string;
  contractNumber?: string;
  clientId: number;
  mrr: number;
}

/**
 * Os signatários para o painel: o que está GRAVADO no ofício, com o status do ClickSign ao
 * vivo por cima quando alguém clicou em "Verificar status".
 *
 * Até 28/09/2026 a tela trocava uma lista pela outra. A resposta do `checkSignatureStatus`
 * traz só nome, e-mail, status e data — então, no clique em Verificar, o CPF e o papel
 * sumiam do painel, e com eles o `clicksignSignerKey`, que é o que monta o botão "Copiar
 * link". O botão aparecia e desaparecia conforme o usuário conferisse o status, que é
 * exatamente quando ele é mais necessário.
 *
 * Casa por e-mail porque é o que o backend usa para casar com o ClickSign (e é a chave que
 * o próprio ClickSign trata como identidade do signatário no documento).
 */
const signatariosDoOficio = (
  oficio: Oficio,
  aoVivo: { signers: Array<{ name: string; email: string; status: string; signedAt?: string }> } | null,
) => {
  const gravados = (oficio.signers || []) as any[];
  if (!aoVivo?.signers?.length) return gravados;

  return gravados.map((gravado) => {
    const atual = aoVivo.signers.find((s) => s.email === gravado.email);
    return atual ? { ...gravado, status: atual.status, signedAt: atual.signedAt || gravado.signedAt } : gravado;
  });
};

/**
 * O status pelo TOM (23/10/2026, padrão da Central): o andamento normal (Rascunho, Emitido, Assinado) é neutro; a cor fica
 * no que pede ação ou é problema. Aguardando assinatura pede acompanhamento (âmbar); Cancelado nunca valeu (vermelho);
 * Revogado existiu, foi assinado e deixou de valer: âmbar, para não ser lido como "nunca chegou a existir".
 */
const TOM_DO_STATUS: Record<string, StatusTone> = {
  'Rascunho': 'neutral',
  'Emitido': 'neutral',
  'Aguardando Assinatura': 'warning',
  'Assinado': 'neutral',
  'Cancelado': 'danger',
  'Revogado': 'warning',
};

export default function OficiosTab() {
  const confirmarPerigo = useConfirmarPerigo();
  const pedirTexto = usePedirTexto();
  const confirmar = useConfirm();
  // D6 (10/10/2026): os 16 alert() viram o aviso do HUB; o alert trava a página e não tem tom.
  const avisar = useAviso();
  const [oficios, setOficios] = useState<Oficio[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('Todos');
  const [tipoFilter, setTipoFilter] = useState('Todos');
  const [modal, setModal] = useState<'create' | 'edit' | 'view' | 'signature' | 'attachments' | null>(null);
  const [selectedOficio, setSelectedOficio] = useState<Oficio | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [pdfPreview, setPdfPreview] = useState<string | null>(null);
  const [previewErro, setPreviewErro] = useState<string | null>(null);
  const [historicoErro, setHistoricoErro] = useState<string | null>(null);
  const [timeline, setTimeline] = useState<DocViewerEvent[]>([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const clientDropdownRef = useRef<HTMLDivElement>(null);

  const emptyForm = {
    tipo: 'Resposta a Renovação',
    clientId: '',
    contractId: '',
    destinatarioNome: '',
    destinatarioEndereco: '',
    destinatarioCidade: '',
    destinatarioCep: '',
    destinatarioEmail: '',
    destinoTipo: 'email' as DestinoTipo,
    destinoPortalUrl: '',
    referenciaOficio: '',
    referenciaProcesso: '',
    assunto: '',
    corpo: '',
    // O bloco de reajuste (valor atual, índice, valor reajustado, data-base) vem da
    // ANUALIDADE vinculada; não se digita mais.
    adjustmentId: '',
    observacoes: '',
  };

  const [form, setForm] = useState<any>(emptyForm);
  const [signerForm, setSignerForm] = useState({
    name: SIGNERS.fabio.name,
    email: SIGNERS.fabio.email,
    cpf: SIGNERS.fabio.cpf,
    birthday: SIGNERS.fabio.birthday,
    phone: '45999999999',
    // Vem do config, não literal: o cargo no contrato social é Sócio-Administrador, e este
    // literal "Administrador" foi o que gravou o cargo errado nos 21 ofícios existentes.
    role: SIGNERS.fabio.cargo,
    authType: 'icp_brasil' as 'email' | 'icp_brasil' | 'whatsapp',
  });
  const [mergeAttachments, setMergeAttachments] = useState(true);

  // D5 (10/10/2026): a carga calava. Com a API fora, os cartões diziam 0, a lista "Nenhum oficio encontrado" e "Novo
  // Oficio" seguia ativo: o operador montava outro ofício ao órgão, com número novo.
  const carga = useEstadoDaCarga();
  const rodarCarga = carga.rodar;
  const loadData = useCallback(async () => {
    await rodarCarga(async () => {
      const [oficiosData, clientsData, contractsData] = await Promise.all([
        oficiosService.getAll(),
        clientsService.getAll(),
        contractsService.getAll(),
      ]);
      setOficios(Array.isArray(oficiosData) ? oficiosData : []);
      setClients(Array.isArray(clientsData) ? clientsData : []);
      setContracts(Array.isArray(contractsData) ? contractsData : []);
    });
  }, [rodarCarga]);
  const loading = carga.estado === 'carregando';
  const temFonte = carga.atualizadoEm !== null;
  const kpi = (n: number) => (temFonte ? n : textos.numeroSemFonte);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Stats
  const totalOficios = oficios.length;
  const totalRascunhos = oficios.filter(o => o.status === 'Rascunho').length;
  const totalAguardando = oficios.filter(o => o.status === 'Aguardando Assinatura').length;
  const totalAssinados = oficios.filter(o => o.status === 'Assinado').length;

  const filtered = oficios.filter((o) => {
    const matchSearch =
      o.numero.toLowerCase().includes(search.toLowerCase()) ||
      o.destinatarioNome.toLowerCase().includes(search.toLowerCase()) ||
      o.assunto.toLowerCase().includes(search.toLowerCase()) ||
      (o.client?.tradeName || '').toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'Todos' || o.status === statusFilter;
    const matchTipo = tipoFilter === 'Todos' || o.tipo === tipoFilter;
    return matchSearch && matchStatus && matchTipo;
  });

  // Close client dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(e.target as Node)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCreate = () => {
    setForm(emptyForm);
    setSelectedOficio(null);
    setClientSearch('');
    setModal('create');
  };

  const handleEdit = (oficio: Oficio) => {
    setForm({
      tipo: oficio.tipo || 'Outro',
      clientId: oficio.clientId || '',
      contractId: oficio.contractId || '',
      destinatarioNome: oficio.destinatarioNome || '',
      destinatarioEndereco: oficio.destinatarioEndereco || '',
      destinatarioCidade: oficio.destinatarioCidade || '',
      destinatarioCep: oficio.destinatarioCep || '',
      destinatarioEmail: oficio.destinatarioEmail || '',
      destinoTipo: oficio.destinoTipo === 'portal' ? 'portal' : 'email',
      destinoPortalUrl: oficio.destinoPortalUrl || '',
      referenciaOficio: oficio.referenciaOficio || '',
      referenciaProcesso: oficio.referenciaProcesso || '',
      assunto: oficio.assunto || '',
      corpo: oficio.corpo || '',
      adjustmentId: (oficio as any).adjustmentId || '',
      observacoes: '',
    });
    setSelectedOficio(oficio);
    const editClient = clients.find((c) => c.id === oficio.clientId);
    setClientSearch(editClient ? ((editClient as any).name || editClient.companyName) : '');
    setModal('edit');
  };

  const handleView = async (oficio: Oficio) => {
    setSelectedOficio(oficio);
    setPdfPreview(null);
    setPreviewErro(null);
    setHistoricoErro(null);
    setSignatureStatus(null);
    setTimeline([]);
    setModal('view');
    // Aguardando assinatura → busca status dos signatários (painel no modal).
    if (oficio.status === 'Aguardando Assinatura') {
      handleCheckStatus(oficio);
    }
    // A timeline carrega SEMPRE, e não só quando o documento está pendente: o estado em que
    // o ofício passa o resto da vida é "Assinado", e era justamente nele que a tela não
    // dizia quem assinou nem quando.
    loadTimeline(oficio.id);
    try {
      const result = await oficiosService.getPdfBase64(oficio.id);
      setPdfPreview(result.base64);
    } catch (e) {
      // Era um console.error, e o modal ficava girando sem o documento.
      setPreviewErro(mensagemDoErroDeCarga(e));
    }
  };

  const loadTimeline = async (id: number) => {
    setTimelineLoading(true);
    try {
      setTimeline(await oficiosService.getHistory(id));
    } catch (e) {
      // Histórico é acessório: se falhar, o documento continua visível, mas a lateral diz que não carregou (antes,
      // parecia "sem histórico").
      setTimeline([]);
      setHistoricoErro(mensagemDoErroDeCarga(e));
    } finally {
      setTimelineLoading(false);
    }
  };

  /** Painel lateral do modal do olho. */
  const infoDoOficio = (oficio: Oficio): DocViewerInfoItem[] => {
    const itens: DocViewerInfoItem[] = [
      {
        label: 'Status',
        value: oficio.status,
        tone: oficio.status === 'Assinado' ? 'success'
          : oficio.status === 'Aguardando Assinatura' ? 'warning'
          : oficio.status === 'Cancelado' ? 'danger'
          : 'default',
      },
      { label: 'Tipo', value: oficio.tipo },
      { label: 'Destinatário', value: oficio.destinatarioNome },
      entregaDoOficio(oficio),
    ];
    if (oficio.client) {
      itens.push({ label: 'Cliente', value: nomeDeExibicao(oficio.client, '—') });
    }
    if (oficio.referenciaProcesso) itens.push({ label: 'Processo', value: oficio.referenciaProcesso });
    itens.push({ label: 'Criado em', value: fmtDate(oficio.createdAt) });
    if (oficio.signedAt) itens.push({ label: 'Assinado em', value: fmtDate(oficio.signedAt), tone: 'success' });
    if (oficio.attachments?.length) itens.push({ label: 'Anexos', value: String(oficio.attachments.length) });
    return itens;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = {
        ...form,
        ...camposDeDestino(form),
        clientId: form.clientId ? parseInt(form.clientId) : null,
        contractId: form.contractId ? parseInt(form.contractId) : null,
        adjustmentId: form.adjustmentId ? parseInt(form.adjustmentId) : null,
      };

      console.log('Enviando dados do oficio:', JSON.stringify(data, null, 2));

      if (selectedOficio) {
        await oficiosService.update(selectedOficio.id, payloadDaEdicao(selectedOficio, data));
      } else {
        await oficiosService.create(data);
      }

      setModal(null);
      loadData();
    } catch (e: any) {
      console.error('Erro ao salvar oficio:', e.response?.data);
      const errorData = e.response?.data;
      let errorMsg = 'Erro desconhecido';
      if (errorData) {
        if (Array.isArray(errorData.message)) {
          errorMsg = errorData.message.join('\n');
        } else if (typeof errorData.message === 'string') {
          errorMsg = errorData.message;
        } else {
          errorMsg = JSON.stringify(errorData);
        }
      } else if (e.message) {
        errorMsg = e.message;
      }
      avisar('Erro ao salvar: ' + errorMsg, 'error');
    }
    setSaving(false);
  };

  const handleDelete = async (oficio: Oficio) => {
    if (!await confirmarPerigo(['Excluir o rascunho?', `${oficio.numero} para ${oficio.destinatarioNome}: "${oficio.assunto}".`].join('\n\n'))) return;
    try {
      await oficiosService.delete(oficio.id);
      loadData();
    } catch (e: any) {
      avisar('Erro ao excluir: ' + (e.response?.data?.message || e.message), 'error');
    }
  };

  const handleEmit = async (oficio: Oficio) => {
    if (porqueNaoOficializar(oficio as any)) return;
    if (!await confirmar(confirmacaoDeEmitirOficio(oficio))) return;
    try {
      await oficiosService.emit(oficio.id);
      loadData();
    } catch (e: any) {
      avisar('Erro ao emitir: ' + (e.response?.data?.message || e.message), 'error');
    }
  };

  const handleCancel = async (oficio: Oficio) => {
    if (!await confirmarPerigo(confirmacaoDeCancelarOficio(oficio))) return;
    try {
      await oficiosService.cancel(oficio.id);
      loadData();
    } catch (e: any) {
      avisar('Erro ao cancelar: ' + (e.response?.data?.message || e.message), 'error');
    }
  };

  const handleRevoke = async (oficio: Oficio) => {
    // Motivo obrigatorio, e o backend recusa com menos de 10 caracteres. Pedir aqui evita
    // a viagem ate o 400 e deixa claro na hora que a explicacao e parte do ato.
    const motivo = await pedirTexto({
      title: 'Revogar o ofício ' + oficio.numero + '?',
      consequence: 'O documento continua existindo, com o número, a assinatura e o PDF assinado ' +
        'preservados. O que muda é que ele passa a constar como sem validade.',
      label: 'Motivo',
      obrigatorio: true,
      validar: (t) => (t.length < 10 ? 'Pelo menos 10 caracteres.' : null),
      danger: true,
      confirmLabel: 'Revogar',
    });
    if (motivo === null) return;

    try {
      await oficiosService.revoke(oficio.id, motivo.trim());
      loadData();
    } catch (e: any) {
      avisar('Erro ao revogar: ' + (e.response?.data?.message || e.message), 'error');
    }
  };

  const handleReopen = async (oficio: Oficio) => {
    if (!await confirmar(confirmacaoDeReabrirOficio(oficio))) return;
    try {
      await oficiosService.reopen(oficio.id);
      loadData();
    } catch (e: any) {
      avisar('Erro ao reabrir: ' + (e.response?.data?.message || e.message), 'error');
    }
  };

  const [resendingId, setResendingId] = useState<number | null>(null);
  const handleResendNotification = async (oficio: Oficio) => {
    setResendingId(oficio.id);
    try {
      // O relato diz QUEM recebeu (e quem não, com o motivo); "N signatário(s)" contava tentativas (fase 0, 23/10/2026).
      const { texto, tom } = relatoDoLembrete(await oficiosService.resendNotification(oficio.id));
      avisar(texto, tom);
    } catch (e: any) {
      const { texto, tom } = relatoDaFalhaDoLembrete(e);
      avisar(texto, tom);
    } finally {
      setResendingId(null);
    }
  };

  // Status da assinatura (painel no modal view) — espelha o Portabilidade.
  const [signatureStatus, setSignatureStatus] = useState<{ status: string; signers: Array<{ name: string; email: string; status: string; signedAt?: string }> } | null>(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const handleCheckStatus = async (oficio: Oficio) => {
    setCheckingStatus(true);
    try {
      const r = await oficiosService.getSignatureStatus(oficio.id);
      setSignatureStatus(r);
      loadData(); // se todos assinaram, o backend promove pra Assinado
    } catch (e: any) {
      avisar('Erro ao verificar status: ' + (e.response?.data?.message || e.message), 'error');
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleOpenSignature = (oficio: Oficio) => {
    if (porqueNaoOficializar(oficio as any)) return;
    setSelectedOficio(oficio);
    setMergeAttachments((oficio.attachments?.length || 0) > 0);
    setModal('signature');
  };

  const handleSendForSignature = async () => {
    if (!selectedOficio) return;
    if (!signerForm.name || !signerForm.email) {
      avisar('Preencha nome e email do signatario', 'error');
      return;
    }
    setSaving(true);
    try {
      const hasAttachments = (selectedOficio.attachments?.length || 0) > 0;
      await oficiosService.sendForSignature(selectedOficio.id, [signerForm], hasAttachments && mergeAttachments);
      setModal(null);
      loadData();
      avisar('Oficio enviado para assinatura com sucesso!', 'success');
    } catch (e: any) {
      avisar('Erro ao enviar para assinatura: ' + (e.response?.data?.message || e.message), 'error');
    }
    setSaving(false);
  };

  const handleDownloadPdf = async (oficio: Oficio) => {
    try {
      const result = await oficiosService.getPdfBase64(oficio.id);

      // Blob + objectURL, e nao a data URI direto no href. O PDF assinado com anexos
      // mesclados chega a 8 MB, e o Chrome tem limite pratico de tamanho para navegacao em
      // `data:`: acima dele o clique simplesmente NAO baixa nada, sem lancar erro, entao o
      // catch abaixo nunca dispara e o usuario acha que o sistema travou.
      const base64 = result.base64.replace(/^data:application\/pdf;base64,/, '');
      const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));

      const link = document.createElement('a');
      link.href = url;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Sem o revoke o blob fica na memoria ate o fim da sessao.
      URL.revokeObjectURL(url);
    } catch (e: any) {
      avisar('Erro ao baixar PDF: ' + (e.response?.data?.message || e.message), 'error');
    }
  };

  const handleOpenAttachments = (oficio: Oficio) => {
    setSelectedOficio(oficio);
    setModal('attachments');
  };

  const refreshSelected = (updated: Oficio) => {
    setSelectedOficio(updated);
    setOficios((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
  };

  const handleUploadAttachments = async (files: FileList | null) => {
    if (!selectedOficio || !files || files.length === 0) return;
    const valid = Array.from(files).filter((f) => {
      if (f.size > 20 * 1024 * 1024) {
        avisar(f.name + ': muito grande. Maximo 20MB', 'error');
        return false;
      }
      return true;
    });
    if (valid.length === 0) return;

    setUploadingAttachment(true);
    try {
      let updated: Oficio | null = null;
      for (const file of valid) {
        const base64 = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.readAsDataURL(file);
        });
        updated = await oficiosService.addAttachment(selectedOficio.id, {
          filename: file.name,
          fileBase64: base64,
        });
      }
      if (updated) refreshSelected(updated);
    } catch (e: any) {
      avisar('Erro ao anexar: ' + (e.response?.data?.message || e.message), 'error');
    }
    setUploadingAttachment(false);
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    if (!selectedOficio) return;
    if (!await confirmarPerigo('Remover este anexo?')) return;
    try {
      const updated = await oficiosService.removeAttachment(selectedOficio.id, attachmentId);
      refreshSelected(updated);
    } catch (e: any) {
      avisar('Erro ao remover anexo: ' + (e.response?.data?.message || e.message), 'error');
    }
  };

  const formatBytes = (bytes: number) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1);
    return (bytes / Math.pow(k, i)).toFixed(i === 0 ? 0 : 1) + ' ' + sizes[i];
  };

  const handleClientChange = (clientId: string) => {
    const client = clients.find((c) => c.id === parseInt(clientId));
    if (client) {
      // Build address string
      let endereco = '';
      if (client.street) {
        endereco = client.street;
        if (client.number) endereco += ', ' + client.number;
        if (client.complement) endereco += ' - ' + client.complement;
        if (client.neighborhood) endereco += ', ' + client.neighborhood;
      }

      // Build city/state string
      let cidade = '';
      if (client.city) {
        cidade = client.city;
        if (client.state) cidade += '/' + client.state;
      }

      setForm((prev: any) => ({
        ...prev,
        clientId,
        destinatarioNome: client.tradeName || client.companyName || prev.destinatarioNome,
        destinatarioEndereco: endereco || prev.destinatarioEndereco,
        destinatarioCidade: cidade || prev.destinatarioCidade,
        destinatarioCep: client.zipCode || prev.destinatarioCep,
      }));
    } else {
      setForm((prev: any) => ({
        ...prev,
        clientId,
      }));
    }
  };

  /**
   * O bloco de reajuste do ofício (valor atual, índice, valor reajustado, data-base) é
   * a ANUALIDADE vinculada (Clientes → Reajustes). Antes eram quatro campos digitados
   * que se contradiziam entre si e com o registro; hoje se escolhe a anualidade e o
   * backend deriva o bloco, o PDF e os placeholders {{ valorAtual }} dela.
   */
  const [contractAdjustments, setContractAdjustments] = useState<any[]>([]);
  // D3 (10/10/2026): a falha calava e o form dizia "Esta assinatura não tem anualidade". A leitura é ADMIN: para quem
  // não é, falhava SEMPRE, e o operador digitava os valores no corpo, sem anualidade e sem a checagem do bloco.
  const [anualidadesErro, setAnualidadesErro] = useState<string | null>(null);
  useEffect(() => {
    const id = parseInt(form.contractId);
    setAnualidadesErro(null);
    if (!id) { setContractAdjustments([]); return; }
    let vivo = true;
    contractAdjustmentsService.getByContract(id)
      .then((list: any) => { if (vivo) setContractAdjustments(Array.isArray(list) ? list : []); })
      .catch((e: any) => { if (vivo) { setContractAdjustments([]); setAnualidadesErro(mensagemDoErroDeCarga(e)); } });
    return () => { vivo = false; };
  }, [form.contractId]);

  const handleContractChange = (contractId: string) => {
    setForm((prev: any) => ({ ...prev, contractId, adjustmentId: '' }));
  };

  const selectedAdjustment = contractAdjustments.find((a: any) => String(a.id) === String(form.adjustmentId));
  const describeAdjustment = (a: any) =>
    `${a.sequence}ª · ${fmtDate(a.anniversaryDate)} · ${a.indexName || ''} ${fmtPctAdj(a.percentage)} · ${fmtBRLAdj(a.amountBefore)} → ${fmtBRLAdj(a.amountAfter)} (${a.status})`;

  const travado = modal === 'edit' && textoTravado(selectedOficio);

  if (loading && !temFonte) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500'></div>
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      {/* Os cartões do topo (padrão das listas, 23/10/2026): o CartaoDoTopo; o escolhido pela barra primary, não o anel
          laranja; o número em branco; Aguardando leva a barra âmbar quando há ofício esperando assinatura. */}
      <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
        <CartaoDoTopo rotulo='Ofícios no Ano' valor={kpi(totalOficios)} sub='Total de ofícios' ativo={statusFilter === 'Todos'} onClick={() => setStatusFilter('Todos')} />
        <CartaoDoTopo rotulo='Rascunhos' valor={kpi(totalRascunhos)} sub='Pendentes de emissão' ativo={statusFilter === 'Rascunho'} onClick={() => setStatusFilter(statusFilter === 'Rascunho' ? 'Todos' : 'Rascunho')} />
        <CartaoDoTopo rotulo='Aguardando' valor={kpi(totalAguardando)} sub='Aguardando assinatura' barra={temFonte && totalAguardando > 0 ? 'warning' : null}
          ativo={statusFilter === 'Aguardando Assinatura'} onClick={() => setStatusFilter(statusFilter === 'Aguardando Assinatura' ? 'Todos' : 'Aguardando Assinatura')} />
        <CartaoDoTopo rotulo='Assinados' valor={kpi(totalAssinados)} sub='Ofícios concluídos' ativo={statusFilter === 'Assinado'} onClick={() => setStatusFilter(statusFilter === 'Assinado' ? 'Todos' : 'Assinado')} />
      </div>

      {/* Search and Filters */}
      <div className='bg-central-cartao rounded-central border border-central-borda p-4'>
        <div className='flex flex-col md:flex-row gap-4'>
          <div className='relative flex-1'>
            <Icon name='search' className='absolute left-3 top-3 w-5 h-5 text-slate-500' />
            <input
              type='text'
              placeholder='Buscar por numero, destinatario, assunto...'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`w-full pl-10 pr-4 py-2.5 placeholder-slate-500 rounded-lg focus:outline-none ${campo}`}
            />
          </div>

          <select
            value={tipoFilter}
            onChange={(e) => setTipoFilter(e.target.value)}
            className={`px-4 py-2.5 ${campo} rounded-lg focus:outline-none`}
          >
            <option value='Todos'>Todos os Tipos</option>
            <option value='Resposta a Renovação'>Resposta a Renovação</option>
            <option value='Comunicado de Reajuste'>Comunicado de Reajuste</option>
            <option value='Resposta a Solicitação'>Resposta a Solicitação</option>
            <option value='Comunicado Geral'>Comunicado Geral</option>
            <option value='Outro'>Outro</option>
          </select>

          <div className='flex gap-2'>
            {['Todos', 'Rascunho', 'Emitido', 'Aguardando Assinatura', 'Assinado'].map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                  statusFilter === status
                    ? 'bg-slate-600 text-white font-semibold border border-slate-500'
                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700 border border-slate-700'
                }`}
              >
                {status === 'Aguardando Assinatura' ? 'Aguardando' : status}
              </button>
            ))}
          </div>

          <button
            onClick={() => loadData()}
            disabled={loading}
            title='Atualizar lista'
            className='flex items-center justify-center px-3 py-2.5 bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 border border-slate-700 disabled:opacity-50'
          >
            <Icon name='refresh' className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>

          {carga.estado === 'pronto' && <button
            onClick={handleCreate}
            className='flex items-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium whitespace-nowrap'
          >
            <Icon name='plus' className='w-5 h-5' />
            Novo Oficio
          </button>}
        </div>
      </div>

      {/* Table */}
      <EstadoDaLista carga={carga} temDados={temFonte} oQue='os ofícios' onTentarDeNovo={loadData} vazio={A_PROPRIA_LISTA_DIZ_O_VAZIO}>
      <CascaDaLista>
        <TabelaDaLista>
          <CabecalhoDaLista>
            <ColunaDaLista rotulo='Número' largura='w-36' />
            {/* w-52 = maior tipo do enum ("Comunicado de Reajuste" / "Resposta a Solicitação", ~158px) + px-4 da célula. */}
            <ColunaDaLista rotulo='Tipo' largura='w-52' />
            <ColunaDaLista rotulo='Destinatário' />
            <ColunaDaLista rotulo='Assunto' />
            <ColunaDaLista rotulo='Status' largura='w-40' alinhamento='centro' />
            <ColunaDaLista rotulo='Data' largura='w-28' />
            {/* As ações de uso diário ficam à vista (ver, PDF, anexos, editar, emitir, enviar, reabrir); o que encerra o
                ofício (excluir, cancelar, revogar) vai para o ⋯, em perigo. */}
            <ColunaDaLista rotulo='Ações' largura='w-64' alinhamento='direita' />
          </CabecalhoDaLista>
          <tbody className='divide-y divide-central-borda'>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className='px-4 py-12 text-center text-slate-500'>
                  Nenhum oficio encontrado
                </td>
              </tr>
            ) : (
              filtered.map((oficio) => {
                const encerrar = [
                  ...(oficio.status === 'Rascunho' ? [{ label: 'Excluir', onClick: () => handleDelete(oficio), danger: true }] : []),
                  ...(oficio.status === 'Emitido' ? [{ label: 'Cancelar (o número não se reaproveita)', onClick: () => handleCancel(oficio), danger: true }] : []),
                  ...(oficio.status === 'Aguardando Assinatura' ? [{ label: 'Cancelar', onClick: () => handleCancel(oficio), danger: true }] : []),
                  // Revogar só em ASSINADO, e é outra coisa que cancelar: cancelar é desistir antes de assinar.
                  ...(oficio.status === 'Assinado' ? [{ label: 'Revogar (documento assinado que deixou de valer)', onClick: () => handleRevoke(oficio), danger: true }] : []),
                ];
                return (
                  <LinhaDaLista key={oficio.id} apagada={oficio.status === 'Cancelado'}>
                    <td className={`${CELULA} whitespace-nowrap`}>
                      <span className='font-mono text-sm text-central-texto'>{oficio.numero}</span>
                    </td>
                    <td className={CELULA}>
                      <span className='block text-slate-300 text-sm truncate' title={oficio.tipo}>{oficio.tipo}</span>
                    </td>
                    <td className={CELULA}>
                      <div className='text-central-texto text-sm font-medium truncate' title={oficio.destinatarioNome}>{oficio.destinatarioNome}</div>
                      {oficio.client && (
                        <div className='text-slate-500 text-xs truncate'>{nomeDeExibicao(oficio.client)}</div>
                      )}
                    </td>
                    <td className={CELULA}>
                      <div className='text-slate-300 text-sm truncate' title={oficio.assunto}>{oficio.assunto}</div>
                    </td>
                    <CelulaStatus texto={oficio.status} tom={TOM_DO_STATUS[oficio.status] || 'neutral'} />
                    <td className={`${CELULA} text-slate-400 text-sm`}>{fmtDate(oficio.createdAt)}</td>
                    {encerrar.length > 0 ? (
                      <CelulaAcoes rotuloDoMenu={`Mais ações do ofício ${oficio.numero}`} itens={encerrar} antes={<>
                        <button onClick={() => handleView(oficio)} className='p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg' title='Visualizar PDF'>
                          <Icon name='eye' className='w-4 h-4' />
                        </button>
                        <button onClick={() => handleDownloadPdf(oficio)} className='p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg' title={rotuloDoPdf(oficio.status).titulo}>
                          <Icon name='download' className='w-4 h-4' />
                        </button>
                        <button onClick={() => handleOpenAttachments(oficio)} className='relative p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg' title='Anexos'>
                          <Icon name='paperclip' className='w-4 h-4' />
                          {(oficio.attachments?.length || 0) > 0 && (
                            <span className='absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center bg-slate-500 text-white text-[10px] font-bold rounded-full' data-anexos>
                              {oficio.attachments!.length}
                            </span>
                          )}
                        </button>
                        {oficio.status === 'Rascunho' && (
                          <>
                            <button onClick={() => handleEdit(oficio)} className='p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg' title='Editar'>
                              <Icon name='edit' className='w-4 h-4' />
                            </button>
                            <button onClick={() => handleEmit(oficio)} disabled={!!porqueNaoOficializar(oficio as any)} className='p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed' title={porqueNaoOficializar(oficio as any) || 'Emitir'}>
                              <Icon name='check' className='w-4 h-4' />
                            </button>
                          </>
                        )}
                        {oficio.status === 'Emitido' && (
                          <>
                            <button onClick={() => handleEdit(oficio)} className='p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg' title='Editar entrega (o texto não se edita depois de emitido)'>
                              <Icon name='edit' className='w-4 h-4' />
                            </button>
                          </>
                        )}
                        {(oficio.status === 'Rascunho' || oficio.status === 'Emitido') && (
                          <button onClick={() => handleOpenSignature(oficio)} disabled={!!porqueNaoOficializar(oficio as any)} className='p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed' title={porqueNaoOficializar(oficio as any) || 'Enviar para Assinatura'}>
                            <Icon name='send' className='w-4 h-4' />
                          </button>
                        )}
                        {oficio.status === 'Aguardando Assinatura' && (
                          <>
                            {/* O botao de visualizar ja abre o status da assinatura; nao repetir o olho aqui. */}
                            <button onClick={() => handleResendNotification(oficio)} disabled={resendingId === oficio.id} className='p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg disabled:opacity-50' title='Reenviar notificacao'>
                              <Icon name='send' className={`w-4 h-4 ${resendingId === oficio.id ? 'animate-spin' : ''}`} />
                            </button>
                          </>
                        )}
                        {oficio.status === 'Cancelado' && (
                          <button onClick={() => handleReopen(oficio)} className='p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg' title='Reabrir oficio'>
                            <Icon name='refresh' className='w-4 h-4' />
                          </button>
                        )}

                      </>} />
                    ) : (
                      <td className={CELULA}><div className='flex justify-end flex-nowrap gap-0.5'>
                        <button onClick={() => handleView(oficio)} className='p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg' title='Visualizar PDF'>
                          <Icon name='eye' className='w-4 h-4' />
                        </button>
                        <button onClick={() => handleDownloadPdf(oficio)} className='p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg' title={rotuloDoPdf(oficio.status).titulo}>
                          <Icon name='download' className='w-4 h-4' />
                        </button>
                        <button onClick={() => handleOpenAttachments(oficio)} className='relative p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg' title='Anexos'>
                          <Icon name='paperclip' className='w-4 h-4' />
                          {(oficio.attachments?.length || 0) > 0 && (
                            <span className='absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center bg-slate-500 text-white text-[10px] font-bold rounded-full' data-anexos>
                              {oficio.attachments!.length}
                            </span>
                          )}
                        </button>
                        {oficio.status === 'Rascunho' && (
                          <>
                            <button onClick={() => handleEdit(oficio)} className='p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg' title='Editar'>
                              <Icon name='edit' className='w-4 h-4' />
                            </button>
                            <button onClick={() => handleEmit(oficio)} disabled={!!porqueNaoOficializar(oficio as any)} className='p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed' title={porqueNaoOficializar(oficio as any) || 'Emitir'}>
                              <Icon name='check' className='w-4 h-4' />
                            </button>
                          </>
                        )}
                        {oficio.status === 'Emitido' && (
                          <>
                            <button onClick={() => handleEdit(oficio)} className='p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg' title='Editar entrega (o texto não se edita depois de emitido)'>
                              <Icon name='edit' className='w-4 h-4' />
                            </button>
                          </>
                        )}
                        {(oficio.status === 'Rascunho' || oficio.status === 'Emitido') && (
                          <button onClick={() => handleOpenSignature(oficio)} disabled={!!porqueNaoOficializar(oficio as any)} className='p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed' title={porqueNaoOficializar(oficio as any) || 'Enviar para Assinatura'}>
                            <Icon name='send' className='w-4 h-4' />
                          </button>
                        )}
                        {oficio.status === 'Aguardando Assinatura' && (
                          <>
                            {/* O botao de visualizar ja abre o status da assinatura; nao repetir o olho aqui. */}
                            <button onClick={() => handleResendNotification(oficio)} disabled={resendingId === oficio.id} className='p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg disabled:opacity-50' title='Reenviar notificacao'>
                              <Icon name='send' className={`w-4 h-4 ${resendingId === oficio.id ? 'animate-spin' : ''}`} />
                            </button>
                          </>
                        )}
                        {oficio.status === 'Cancelado' && (
                          <button onClick={() => handleReopen(oficio)} className='p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg' title='Reabrir oficio'>
                            <Icon name='refresh' className='w-4 h-4' />
                          </button>
                        )}

                      </div></td>
                    )}
                  </LinhaDaLista>
                );
              })
            )}
          </tbody>
        </TabelaDaLista>
      </CascaDaLista>
      </EstadoDaLista>

      {/* Modal Create/Edit */}
      {(modal === 'create' || modal === 'edit') && (
        // Editor: não fecha ao clicar fora, como já era. Sai a altura chumbada do
        // corpo (max-h calc(90vh-180px)).
        <CascaDeModal
          largura='lg'
          titulo={modal === 'create' ? 'Novo Oficio' : 'Editar Oficio'}
          onClose={() => setModal(null)}
          corpoClassName='p-6 space-y-6'
          rodape={
            <div className='ml-auto flex gap-3'>
              <button
                onClick={() => setModal(null)}
                className='px-4 py-2.5 text-sm font-medium text-slate-300 bg-slate-700 rounded-lg hover:bg-slate-600'
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.clientId || !form.destinatarioNome || !form.assunto || !form.corpo}
                className='px-4 py-2.5 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-strong disabled:opacity-50'
              >
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          }
        >
              {travado && (
                <p className={`text-sm ${TONE_TEXT.warning}`} data-testid='frase-do-emitido'>{FRASE_DO_EMITIDO}</p>
              )}
              {/* Tipo */}
              <div>
                <label className='block text-sm font-medium text-slate-300 mb-2'>Tipo de Oficio</label>
                <select
                  value={form.tipo}
                  disabled={travado}
                  onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                  className={`w-full px-4 py-2.5 ${travado ? campoSomenteLeitura : campo} rounded-lg focus:outline-none`}
                >
                  <option value='Resposta a Renovação'>Resposta a Renovação</option>
                  <option value='Comunicado de Reajuste'>Comunicado de Reajuste</option>
                  <option value='Resposta a Solicitação'>Resposta a Solicitação</option>
                  <option value='Comunicado Geral'>Comunicado Geral</option>
                  <option value='Outro'>Outro</option>
                </select>
              </div>

              {/* Cliente e Contrato */}
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <div ref={clientDropdownRef} className='relative'>
                  <label className='block text-sm font-medium text-slate-300 mb-2'>Cliente *</label>
                  <input
                    type='text'
                    value={clientSearch}
                    disabled={travado}
                    onChange={(e) => { setClientSearch(e.target.value); setShowClientDropdown(true); if (!e.target.value) { handleClientChange(''); } }}
                    onFocus={() => setShowClientDropdown(true)}
                    placeholder='Buscar cliente...'
                    className={`w-full px-4 py-2.5 ${travado ? campoSomenteLeitura : campo} rounded-lg focus:outline-none`}
                  />
                  {showClientDropdown && (
                    <div className='absolute z-50 w-full mt-1 bg-central-elevado border border-slate-600 rounded-lg shadow-xl max-h-60 overflow-y-auto'>
                      {/* [...clients] e não clients.sort(): sort() muta o array no lugar, e
                          aqui o array é o próprio state, sendo mutado durante o render. */}
                      {[...clients]
                        .sort((a, b) => nomeDeExibicao(a).localeCompare(nomeDeExibicao(b)))
                        .filter((c) => {
                          if (!clientSearch) return true;
                          const q = clientSearch.toLowerCase();
                          return ((c as any).name || '').toLowerCase().includes(q) || ((c as any).tradeName || '').toLowerCase().includes(q) || (c.companyName || '').toLowerCase().includes(q) || (c.cnpj || '').includes(q);
                        })
                        .map((c) => (
                          <button
                            key={c.id}
                            type='button'
                            onClick={() => { handleClientChange(String(c.id)); setClientSearch(nomeDeExibicao(c)); setShowClientDropdown(false); }}
                            className={'w-full text-left px-4 py-2 hover:bg-slate-700 text-sm ' + (String(form.clientId) === String(c.id) ? 'bg-slate-700 text-white font-semibold' : 'text-slate-300')}
                          >
                            {nomeDeExibicao(c)}
                          </button>
                        ))}
                      {clients.filter((c) => { if (!clientSearch) return true; const q = clientSearch.toLowerCase(); return ((c as any).name || '').toLowerCase().includes(q) || ((c as any).tradeName || '').toLowerCase().includes(q) || (c.companyName || '').toLowerCase().includes(q) || (c.cnpj || '').includes(q); }).length === 0 && (
                        <div className='px-4 py-3 text-sm text-slate-500'>Nenhum cliente encontrado</div>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <label className='block text-sm font-medium text-slate-300 mb-2'>Contrato (opcional)</label>
                  <select
                    value={form.contractId}
                    disabled={travado}
                    onChange={(e) => handleContractChange(e.target.value)}
                    className={`w-full px-4 py-2.5 ${travado ? campoSomenteLeitura : campo} rounded-lg focus:outline-none`}
                  >
                    <option value=''>Sem contrato vinculado</option>
                    {contracts
                      .filter((c) => !form.clientId || c.clientId === parseInt(form.clientId))
                      .map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.contractNumber || c.code} - R$ {fmt(c.mrr)}/mes
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              {/* Destinatario */}
              <div className='border-t border-central-borda pt-4'>
                <h4 className='text-sm font-semibold text-white mb-3'>Destinatario</h4>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div className='md:col-span-2'>
                    <label className='block text-sm font-medium text-slate-300 mb-2'>Nome/Orgao *</label>
                    <input
                      type='text'
                      value={form.destinatarioNome}
                      disabled={travado}
                      onChange={(e) => setForm({ ...form, destinatarioNome: e.target.value })}
                      className={`w-full px-4 py-2.5 ${travado ? campoSomenteLeitura : campo} rounded-lg focus:outline-none`}
                      required
                    />
                  </div>
                  {/* Destino de entrega: declarado aqui, nunca inferido. Emitir e enviar para assinatura
                      exigem o campo do destino escolhido (backend #362). */}
                  <div className='md:col-span-2'>
                    <span id='destino-rotulo' className='block text-sm font-medium text-slate-300 mb-2'>Entrega *</span>
                    <div role='group' aria-labelledby='destino-rotulo' className='flex flex-wrap gap-2'>
                      {DESTINOS.map((d) => (
                        <button
                          key={d.valor}
                          type='button'
                          aria-pressed={form.destinoTipo === d.valor}
                          onClick={() => setForm({ ...form, destinoTipo: d.valor })}
                          className={`px-3 py-2 rounded-lg text-sm transition-colors ${form.destinoTipo === d.valor ? 'bg-slate-600 text-white font-semibold' : 'bg-slate-800 text-slate-400 hover:bg-slate-700 border border-slate-700'}`}
                        >
                          {d.rotulo}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className='md:col-span-2'>
                    <label htmlFor='destinatario-email' className='block text-sm font-medium text-slate-300 mb-2'>
                      E-mail do destinatário{form.destinoTipo === 'email' ? ' *' : ''}
                    </label>
                    <input
                      id='destinatario-email'
                      type='email'
                      value={form.destinatarioEmail}
                      onChange={(e) => setForm({ ...form, destinatarioEmail: e.target.value })}
                      placeholder='Ex: protocolo@orgao.gov.br'
                      className={`w-full px-4 py-2.5 placeholder-slate-500 rounded-lg focus:border-primary focus:outline-none ${campo}`}
                    />
                    <p className='mt-1 text-xs text-slate-500'>
                      {form.destinoTipo === 'email'
                        ? 'Obrigatório para emitir e enviar para assinatura. Se o endereço estiver numa thread de e-mail, copie-o daqui.'
                        : 'Opcional com entrega pelo portal.'}
                    </p>
                  </div>
                  {form.destinoTipo === 'portal' && (
                    <div className='md:col-span-2'>
                      <label htmlFor='destino-portal-url' className='block text-sm font-medium text-slate-300 mb-2'>Link da compra no portal *</label>
                      <input
                        id='destino-portal-url'
                        type='url'
                        value={form.destinoPortalUrl}
                        onChange={(e) => setForm({ ...form, destinoPortalUrl: e.target.value })}
                        placeholder='https://cnetmobile.estaleiro.serpro.gov.br/comprasnet-web/public/compras/acompanhamento-compra?compra=…'
                        className={`w-full px-4 py-2.5 placeholder-slate-500 rounded-lg focus:border-primary focus:outline-none ${campo}`}
                      />
                      <p className='mt-1 text-xs text-slate-500'>
                        Onde o documento será anexado. Obrigatório para emitir e enviar para assinatura; precisa começar com https://.
                      </p>
                    </div>
                  )}
                  <div className='md:col-span-2'>
                    <label className='block text-sm font-medium text-slate-300 mb-2'>Endereco</label>
                    <input
                      type='text'
                      value={form.destinatarioEndereco}
                      disabled={travado}
                      onChange={(e) => setForm({ ...form, destinatarioEndereco: e.target.value })}
                      className={`w-full px-4 py-2.5 ${travado ? campoSomenteLeitura : campo} rounded-lg focus:outline-none`}
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-slate-300 mb-2'>Cidade/UF</label>
                    <input
                      type='text'
                      value={form.destinatarioCidade}
                      disabled={travado}
                      onChange={(e) => setForm({ ...form, destinatarioCidade: e.target.value })}
                      className={`w-full px-4 py-2.5 ${travado ? campoSomenteLeitura : campo} rounded-lg focus:outline-none`}
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-slate-300 mb-2'>CEP</label>
                    <input
                      type='text'
                      value={form.destinatarioCep}
                      disabled={travado}
                      onChange={(e) => setForm({ ...form, destinatarioCep: e.target.value })}
                      className={`w-full px-4 py-2.5 ${travado ? campoSomenteLeitura : campo} rounded-lg focus:outline-none`}
                    />
                  </div>
                </div>
              </div>

              {/* Referencia */}
              <div className='border-t border-central-borda pt-4'>
                <h4 className='text-sm font-semibold text-white mb-3'>Referencia</h4>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div>
                    <label className='block text-sm font-medium text-slate-300 mb-2'>Oficio de Referencia</label>
                    <input
                      type='text'
                      value={form.referenciaOficio}
                      disabled={travado}
                      onChange={(e) => setForm({ ...form, referenciaOficio: e.target.value })}
                      placeholder='Ex: Oficio n. 24/2026'
                      className={`w-full px-4 py-2.5 placeholder-slate-500 rounded-lg focus:outline-none ${travado ? campoSomenteLeitura : campo}`}
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-slate-300 mb-2'>Processo</label>
                    <input
                      type='text'
                      value={form.referenciaProcesso}
                      disabled={travado}
                      onChange={(e) => setForm({ ...form, referenciaProcesso: e.target.value })}
                      placeholder='Ex: 62455.000591/2025-91'
                      className={`w-full px-4 py-2.5 placeholder-slate-500 rounded-lg focus:outline-none ${travado ? campoSomenteLeitura : campo}`}
                    />
                  </div>
                </div>
              </div>

              {/* Assunto e Corpo */}
              <div className='border-t border-central-borda pt-4'>
                <h4 className='text-sm font-semibold text-white mb-3'>Conteudo</h4>
                <div className='space-y-4'>
                  <div>
                    <label className='block text-sm font-medium text-slate-300 mb-2'>Assunto *</label>
                    <input
                      type='text'
                      value={form.assunto}
                      disabled={travado}
                      onChange={(e) => setForm({ ...form, assunto: e.target.value })}
                      placeholder='Ex: Manifestacao de Interesse na Prorrogacao Contratual'
                      className={`w-full px-4 py-2.5 placeholder-slate-500 rounded-lg focus:outline-none ${travado ? campoSomenteLeitura : campo}`}
                      required
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-slate-300 mb-2'>Corpo do Oficio *</label>
                    <textarea
                      value={form.corpo}
                      disabled={travado}
                      onChange={(e) => setForm({ ...form, corpo: e.target.value })}
                      rows={8}
                      className={`w-full px-4 py-2.5 placeholder-slate-500 rounded-lg focus:outline-none ${travado ? campoSomenteLeitura : campo}`}
                      placeholder='Digite o conteudo do oficio...'
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Dados de Reajuste = a ANUALIDADE vinculada. O bloco impresso (valor
                  atual, índice, valor reajustado, data-base) e os placeholders
                  {{ valorAtual }} etc. vêm dela; não há mais campos digitados. */}
              {(form.tipo === 'Comunicado de Reajuste' || form.adjustmentId || contractAdjustments.length > 0) && (
                <div className='border-t border-central-borda pt-4'>
                  <h4 className='text-sm font-semibold text-white mb-3'>Dados de Reajuste</h4>
                  {!form.contractId ? (
                    <p className='text-xs text-slate-400'>Escolha a assinatura acima para listar as anualidades de reajuste.</p>
                  ) : anualidadesErro ? (
                    <p role='alert' className={`text-xs ${TONE_TEXT.warning}`}>{fraseDaAnualidadeQueNaoCarregou(anualidadesErro)}</p>
                  ) : contractAdjustments.length === 0 ? (
                    <p className='text-xs text-amber-400'>Esta assinatura não tem anualidade cadastrada. Gere em Clientes → Reajustes (ou na assinatura, "Gerar anualidades vencidas") antes de montar o ofício.</p>
                  ) : (
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                      <div>
                        <label className='block text-sm font-medium text-slate-300 mb-2'>Anualidade</label>
                        <select
                          value={form.adjustmentId || ''}
                          disabled={travado}
                          onChange={(e) => setForm({ ...form, adjustmentId: e.target.value })}
                          className={`w-full px-4 py-2.5 ${travado ? campoSomenteLeitura : campo} rounded-lg focus:outline-none`}
                        >
                          <option value=''>— sem bloco de reajuste —</option>
                          {contractAdjustments.map((a: any) => (
                            <option key={a.id} value={a.id}>{describeAdjustment(a)}</option>
                          ))}
                        </select>
                        <p className='text-xs text-slate-500 mt-1'>Para corrigir valores ou índice, edite a anualidade em Clientes → Reajustes.</p>
                      </div>
                      {selectedAdjustment && (
                        <div className='text-sm text-slate-300 space-y-1 bg-central-fundo/60 border border-central-borda rounded-lg p-3'>
                          <p>Valor mensal atual: <span className='text-white'>{fmtBRLAdj(selectedAdjustment.amountBefore)}</span></p>
                          <p>Índice ({selectedAdjustment.indexName || '—'}): <span className='text-white'>{fmtPctAdj(selectedAdjustment.percentage)}</span>{selectedAdjustment.isProvisional && <span className='ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400'>provisório</span>}</p>
                          <p>Valor mensal reajustado: <span className='text-white font-medium'>{fmtBRLAdj(selectedAdjustment.amountAfter)}</span></p>
                          <p>Data-base (aniversário): <span className='text-white'>{fmtDate(selectedAdjustment.anniversaryDate)}</span> · efeitos desde {selectedAdjustment.effectsFrom ? fmtDate(selectedAdjustment.effectsFrom) : '—'}</p>
                          <p className='text-xs text-slate-500'>{adjustmentStatusMeta(selectedAdjustment.status).label}: {adjustmentStatusMeta(selectedAdjustment.status).hint}</p>
                        </div>
                      )}
                    </div>
                  )}
                  {form.tipo === 'Comunicado de Reajuste' && (
                    <p className='text-xs text-slate-400 mt-3'>
                      Comunicado de Reajuste exige anualidade vinculada. A emissão é recusada sem ela ou se a conta não fechar.
                    </p>
                  )}
                </div>
              )}
        </CascaDeModal>
      )}

      {/* Modal View PDF (componente compartilhado — ver shared/DocumentViewerModal.tsx) */}
      {modal === 'view' && selectedOficio && (
        <DocumentViewerModal
          title={selectedOficio.numero}
          subtitle={selectedOficio.assunto}
          pdfBase64={pdfPreview}
          loading={!pdfPreview && !previewErro}
          unsupportedMessage={previewErro ? `Não consegui carregar o PDF: ${previewErro}.` : undefined}
          accent='primary'
          onClose={() => setModal(null)}
          info={infoDoOficio(selectedOficio)}
          // Os signatários vinham do ClickSign ao vivo APENAS em "Aguardando Assinatura", e
          // o painel inteiro sumia da tela nos outros status. O `signers` gravado no ofício
          // é a fonte para o resto da vida do documento, inclusive assinado.
          signers={signatariosDoOficio(selectedOficio, signatureStatus)}
          documentSignedAt={selectedOficio.signedAt}
          timeline={timeline}
          timelineLoading={timelineLoading}
          sidebarExtra={historicoErro ? (
            <p role='status' className={`text-xs ${TONE_TEXT.warning}`}>Não consegui carregar o histórico: {historicoErro}.</p>
          ) : undefined}
          headerActions={
            <button
              onClick={() => handleDownloadPdf(selectedOficio)}
              className='flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200'
              title={rotuloDoPdf(selectedOficio.status).titulo}
            >
              <Icon name='download' className='w-4 h-4' />
              {rotuloDoPdf(selectedOficio.status).curto}
            </button>
          }
          sidebarActions={
            selectedOficio.status === 'Aguardando Assinatura' ? (
              <>
                <button
                  onClick={() => handleCheckStatus(selectedOficio)}
                  disabled={checkingStatus}
                  className='flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-xs rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200 disabled:opacity-50'
                >
                  <Icon name='refresh' className={`w-3 h-3 ${checkingStatus ? 'animate-spin' : ''}`} />
                  {checkingStatus ? 'Verificando...' : 'Verificar status'}
                </button>
                <button
                  onClick={() => handleResendNotification(selectedOficio)}
                  disabled={resendingId === selectedOficio.id}
                  className='flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-xs rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200 disabled:opacity-50'
                >
                  <Icon name='send' className='w-3 h-3' />
                  Reenviar
                </button>
              </>
            ) : null
          }
        />
      )}

      {/* Modal Signature */}
      {modal === 'signature' && selectedOficio && (
        // Signatário digitado: não fecha ao clicar fora, como já era. Envio para fora
        // (ClickSign): só moldura, handleSendForSignature não muda. A primária roxa
        // vira o laranja; o número do ofício e a contagem de anexos saem do laranja.
        <CascaDeModal
          largura='xs'
          titulo='Enviar para Assinatura'
          subtitulo={selectedOficio.numero}
          onClose={() => setModal(null)}
          corpoClassName='p-6 space-y-4'
          rodape={
            <div className='ml-auto flex gap-3'>
              <button
                onClick={() => setModal(null)}
                className='px-4 py-2.5 text-sm font-medium text-slate-300 bg-slate-700 rounded-lg hover:bg-slate-600'
              >
                Cancelar
              </button>
              <button
                onClick={handleSendForSignature}
                disabled={saving || !signerForm.name || !signerForm.email}
                className='px-4 py-2.5 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-strong disabled:opacity-50'
              >
                {saving ? 'Enviando...' : 'Enviar para Assinatura'}
              </button>
            </div>
          }
        >
              <p className='text-sm text-slate-400'>
                O oficio <span className='text-white font-medium'>{selectedOficio.numero}</span> sera enviado para assinatura digital via ClickSign.
              </p>

              <div>
                <label className='block text-sm font-medium text-slate-300 mb-2'>Nome do Signatario *</label>
                <input
                  type='text'
                  value={signerForm.name}
                  onChange={(e) => setSignerForm({ ...signerForm, name: e.target.value })}
                  className={`w-full px-4 py-2.5 ${campo} rounded-lg focus:outline-none`}
                />
              </div>

              <div>
                <label className='block text-sm font-medium text-slate-300 mb-2'>Email *</label>
                <input
                  type='email'
                  value={signerForm.email}
                  onChange={(e) => setSignerForm({ ...signerForm, email: e.target.value })}
                  className={`w-full px-4 py-2.5 ${campo} rounded-lg focus:outline-none`}
                />
              </div>

              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label className='block text-sm font-medium text-slate-300 mb-2'>CPF</label>
                  <input
                    type='text'
                    value={signerForm.cpf}
                    onChange={(e) => setSignerForm({ ...signerForm, cpf: e.target.value })}
                    className={`w-full px-4 py-2.5 ${campo} rounded-lg focus:outline-none`}
                  />
                </div>
                <div>
                  <label className='block text-sm font-medium text-slate-300 mb-2'>Data Nascimento</label>
                  <input
                    type='date'
                    value={signerForm.birthday}
                    onChange={(e) => setSignerForm({ ...signerForm, birthday: e.target.value })}
                    className={`w-full px-4 py-2.5 ${campo} rounded-lg focus:outline-none`}
                  />
                </div>
              </div>

              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label className='block text-sm font-medium text-slate-300 mb-2'>Cargo/Funcao</label>
                  <input
                    type='text'
                    value={signerForm.role}
                    onChange={(e) => setSignerForm({ ...signerForm, role: e.target.value })}
                    className={`w-full px-4 py-2.5 ${campo} rounded-lg focus:outline-none`}
                  />
                </div>
                <div>
                  <label className='block text-sm font-medium text-slate-300 mb-2'>Metodo de Assinatura</label>
                  <select
                    value={signerForm.authType}
                    onChange={(e) => setSignerForm({ ...signerForm, authType: e.target.value as 'email' | 'icp_brasil' | 'whatsapp' })}
                    className={`w-full px-4 py-2.5 ${campo} rounded-lg focus:outline-none`}
                  >
                    <option value='icp_brasil'>Certificado Digital (ICP-Brasil)</option>
                    <option value='email'>Codigo por Email</option>
                    <option value='whatsapp'>Codigo por WhatsApp</option>
                  </select>
                </div>
              </div>

              {signerForm.authType === 'whatsapp' && (
                <div>
                  <label className='block text-sm font-medium text-slate-300 mb-2'>Telefone (WhatsApp) *</label>
                  <input
                    type='text'
                    value={signerForm.phone}
                    onChange={(e) => setSignerForm({ ...signerForm, phone: e.target.value })}
                    placeholder='Ex: 45999999999'
                    className={`w-full px-4 py-2.5 placeholder-slate-500 rounded-lg focus:outline-none ${campo}`}
                  />
                </div>
              )}

              {(selectedOficio.attachments?.length || 0) > 0 && (
                <label className='flex items-start gap-3 p-3 bg-central-fundo/60 border border-central-borda rounded-lg cursor-pointer'>
                  <input
                    type='checkbox'
                    checked={mergeAttachments}
                    onChange={(e) => setMergeAttachments(e.target.checked)}
                    className='mt-0.5 w-4 h-4 accent-primary'
                  />
                  <span className='text-sm text-slate-300'>
                    Mesclar os <span className='text-white font-medium'>{selectedOficio.attachments!.length} anexo(s)</span> no documento de assinatura
                    <span className='block text-xs text-slate-500 mt-0.5'>
                      Os anexos (PDF/imagens) entram como paginas apos o oficio, formando um unico arquivo assinado.
                    </span>
                  </span>
                </label>
              )}
        </CascaDeModal>
      )}

      {/* Modal Attachments */}
      {modal === 'attachments' && selectedOficio && (
        // Lista com upload: cada anexo grava na hora, nada fica digitado. Passa a
        // fechar ao clicar fora, exceto enquanto envia (uploadingAttachment).
        <CascaDeModal
          largura='base'
          fecharAoClicarFora
          titulo='Anexos'
          subtitulo={selectedOficio.numero}
          onClose={() => { if (!uploadingAttachment) setModal(null); }}
          corpoClassName='p-6 space-y-4'
          rodape={
            <button onClick={() => setModal(null)} className='ml-auto px-4 py-2.5 text-sm font-medium text-slate-300 bg-slate-700 rounded-lg hover:bg-slate-600'>
              Fechar
            </button>
          }
        >
              {/* Upload */}
              <label
                className={`flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed border-slate-600 rounded-lg cursor-pointer hover:border-primary transition-colors ${uploadingAttachment ? 'opacity-50 pointer-events-none' : ''}`}
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-primary'); }}
                onDragLeave={(e) => { e.currentTarget.classList.remove('border-primary'); }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('border-primary');
                  if (!uploadingAttachment && e.dataTransfer?.files?.length) handleUploadAttachments(e.dataTransfer.files);
                }}
              >
                <Icon name='paperclip' className='w-6 h-6 text-slate-400' />
                <span className='text-sm text-slate-300'>
                  {uploadingAttachment ? 'Enviando...' : 'Clique ou arraste para anexar (PDF, PNG ou JPG - max 20MB)'}
                </span>
                <input
                  type='file'
                  multiple
                  accept='application/pdf,image/png,image/jpeg'
                  className='hidden'
                  disabled={uploadingAttachment}
                  onChange={(e) => { handleUploadAttachments(e.target.files); e.target.value = ''; }}
                />
              </label>

              {/* List */}
              {(selectedOficio.attachments?.length || 0) === 0 ? (
                <div className='text-center text-slate-500 text-sm py-6'>Nenhum anexo ainda</div>
              ) : (
                <div className='space-y-2'>
                  {selectedOficio.attachments!.map((att) => (
                    <div key={att.id} className='flex items-center gap-3 p-3 bg-central-fundo/60 rounded-lg border border-central-borda'>
                      <Icon name='fileText' className='w-5 h-5 text-slate-400 flex-shrink-0' />
                      <div className='flex-1 min-w-0'>
                        <div className='text-white text-sm font-medium truncate'>{att.originalName}</div>
                        <div className='text-slate-500 text-xs'>{formatBytes(att.size)} · {fmtDate(att.uploadedAt)}</div>
                      </div>
                      <button onClick={() => oficiosService.viewAttachment(selectedOficio.id, att.id)} className='p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg' title='Visualizar'>
                        <Icon name='eye' className='w-4 h-4' />
                      </button>
                      <button onClick={() => oficiosService.downloadAttachment(selectedOficio.id, att.id, att.originalName)} className='p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg' title='Baixar'>
                        <Icon name='download' className='w-4 h-4' />
                      </button>
                      <button onClick={() => handleDeleteAttachment(att.id)} className='p-2 text-slate-400 hover:text-red-400 hover:bg-slate-700 rounded-lg' title='Remover'>
                        <Icon name='trash' className='w-4 h-4' />
                      </button>
                    </div>
                  ))}
                </div>
              )}
        </CascaDeModal>
      )}
    </div>
  );
}
